import { delivery, type contentTemplates } from '@bb/contracts';
import { insertTemplate, pruneSources } from '../../templates/templateContent.ts';

/**
 * Pure helpers behind the campaign compose step (BB-98): channels, variants and control of a campaign revision's
 * `messages`. The draft has the shape of `delivery.CampaignMessages`; it may be invalid while it is being edited, and
 * `validateMessagesDraft` reports the same issues as the server.
 */
export type DeliveryChannel = delivery.DeliveryChannel;
export type ChannelContent = Record<string, unknown>;

export interface VariantDraft {
  apiId: string;
  name: string;
  percent: number;
  content: Partial<Record<DeliveryChannel, ChannelContent>>;
  /** The template version a channel's content was copied from (BB-56). */
  sourceTemplates?: Partial<Record<DeliveryChannel, contentTemplates.TemplateSource>>;
}

export interface MessagesDraft {
  schemaVersion: 1;
  channels: DeliveryChannel[];
  variants: VariantDraft[];
  control: { percent: number };
}

export const CHANNEL_LABELS: Record<DeliveryChannel, string> = {
  push_android: 'Android push',
  push_ios: 'iOS push',
  push_web: 'Web push',
  content_card: 'Content Card',
  in_app: 'In-app message',
  webhook: 'Webhook',
};

const orderChannels = (channels: readonly DeliveryChannel[]): DeliveryChannel[] => delivery.DELIVERY_CHANNELS.filter((c) => channels.includes(c));

const cloneContent = (content: VariantDraft['content'], channels: readonly DeliveryChannel[]): VariantDraft['content'] =>
  Object.fromEntries(channels.map((c) => [c, structuredClone(content[c] ?? {})]));

export const isContentEmpty = (content: ChannelContent | undefined): boolean => !content || Object.keys(content).length === 0;

/** A new draft: one variant with 100 % and no control. */
export function createMessagesDraft(channels: readonly DeliveryChannel[] = []): MessagesDraft {
  const ordered = orderChannels(channels);
  return { schemaVersion: 1, channels: ordered, variants: [{ apiId: 'variant-1', name: 'Variant 1', percent: 100, content: cloneContent({}, ordered) }], control: { percent: 0 } };
}

/** Variants with non-empty content for the channel (removing it would lose their content). */
export const variantsWithContent = (draft: MessagesDraft, channel: DeliveryChannel): VariantDraft[] => draft.variants.filter((v) => !isContentEmpty(v.content[channel]));

/** Adds the channel and an empty content object for it to every variant. */
export function addChannel(draft: MessagesDraft, channel: DeliveryChannel): MessagesDraft {
  if (draft.channels.includes(channel)) return draft;
  return {
    ...draft,
    channels: orderChannels([...draft.channels, channel]),
    variants: draft.variants.map((v) => ({ ...v, content: { ...v.content, [channel]: v.content[channel] ?? {} } })),
  };
}

/** Removes the channel and its content from every variant. */
export function removeChannel(draft: MessagesDraft, channel: DeliveryChannel): MessagesDraft {
  return {
    ...draft,
    channels: draft.channels.filter((c) => c !== channel),
    variants: draft.variants.map((v) => {
      const { sourceTemplates, ...rest } = v;
      const kept = pruneSources(sourceTemplates, draft.channels.filter((c) => c !== channel));
      return { ...rest, content: Object.fromEntries(Object.entries(v.content).filter(([c]) => c !== channel)), ...(kept ? { sourceTemplates: kept } : {}) };
    }),
  };
}

const API_ID_MAX = 64;

/** A variant API id from its name: lowercase letters, digits, `_` and `-`, starting with a letter or digit. */
export function slugifyVariantName(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '-')
    .replace(/^[-_]+/, '')
    .slice(0, API_ID_MAX)
    .replace(/-+$/, '');
  return slug || 'variant';
}

/** `base`, or `base-2`, `base-3`… when it is taken or reserved (`control`). */
export function uniqueVariantApiId(base: string, taken: ReadonlySet<string>): string {
  const free = (id: string) => id !== delivery.CONTROL_VARIANT_API_ID && !taken.has(id);
  if (free(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, API_ID_MAX - suffix.length)}${suffix}`;
    if (free(candidate)) return candidate;
  }
}

const otherApiIds = (draft: MessagesDraft, index: number) => new Set(draft.variants.filter((_, i) => i !== index).map((v) => v.apiId));

/** The API id a variant gets from `name` while its id was not edited by hand. */
export const autoApiId = (draft: MessagesDraft, index: number, name: string): string => uniqueVariantApiId(slugifyVariantName(name), otherApiIds(draft, index));

const updateVariant = (draft: MessagesDraft, index: number, update: (v: VariantDraft) => VariantDraft): MessagesDraft => ({
  ...draft,
  variants: draft.variants.map((v, i) => (i === index ? update(v) : v)),
});

/** Renames a variant; its API id follows the name unless it was edited by hand. */
export function renameVariant(draft: MessagesDraft, index: number, name: string): MessagesDraft {
  return updateVariant(draft, index, (v) => ({ ...v, name, apiId: v.apiId === autoApiId(draft, index, v.name) ? autoApiId(draft, index, name) : v.apiId }));
}

export const setVariantApiId = (draft: MessagesDraft, index: number, apiId: string): MessagesDraft => updateVariant(draft, index, (v) => ({ ...v, apiId }));

export const setVariantPercent = (draft: MessagesDraft, index: number, percent: number): MessagesDraft => updateVariant(draft, index, (v) => ({ ...v, percent }));

export const setControlPercent = (draft: MessagesDraft, percent: number): MessagesDraft => ({ ...draft, control: { percent } });

export const setVariantContent = (draft: MessagesDraft, index: number, channel: DeliveryChannel, content: ChannelContent): MessagesDraft =>
  updateVariant(draft, index, (v) => ({ ...v, content: { ...v.content, [channel]: content } }));

/** Inserts a template into variant `index` (BB-56): a copy for each campaign channel the template fills, with its source; `null` when it fills none. */
export function insertVariantTemplate(draft: MessagesDraft, index: number, instance: contentTemplates.TemplateInstance): MessagesDraft | null {
  const variant = draft.variants[index];
  const next = variant ? insertTemplate({ channels: draft.channels, content: variant.content, sourceTemplates: variant.sourceTemplates }, instance) : null;
  return next ? updateVariant(draft, index, (v) => ({ ...v, content: next.content, sourceTemplates: next.sourceTemplates ?? {} })) : null;
}

const nextVariantName = (draft: MessagesDraft): string => {
  const names = new Set(draft.variants.map((v) => v.name));
  let n = draft.variants.length + 1;
  while (names.has(`Variant ${n}`)) n++;
  return `Variant ${n}`;
};

/** Adds a variant with 0 % that copies the content of variant `copyFrom` or starts empty; no-op at `MAX_VARIANTS`. */
export function addVariant(draft: MessagesDraft, copyFrom?: number): MessagesDraft {
  if (draft.variants.length >= delivery.MAX_VARIANTS) return draft;
  const name = nextVariantName(draft);
  const source = copyFrom === undefined ? undefined : draft.variants[copyFrom];
  const variant: VariantDraft = {
    apiId: uniqueVariantApiId(slugifyVariantName(name), new Set(draft.variants.map((v) => v.apiId))),
    name,
    percent: 0,
    content: cloneContent(source?.content ?? {}, draft.channels),
  };
  return { ...draft, variants: [...draft.variants, variant] };
}

/** Removes a variant; the last one stays. */
export const removeVariant = (draft: MessagesDraft, index: number): MessagesDraft =>
  draft.variants.length <= 1 ? draft : { ...draft, variants: draft.variants.filter((_, i) => i !== index) };

/** Replaces the content of every channel of variant `target` with a copy of variant `source`'s. */
export function copyVariantContent(draft: MessagesDraft, target: number, source: number): MessagesDraft {
  const from = draft.variants[source];
  if (!from || target === source) return draft;
  return updateVariant(draft, target, (v) => ({ ...v, content: cloneContent(from.content, draft.channels) }));
}

const cents = (percent: number) => (Number.isFinite(percent) ? Math.round(percent * 100) : 0);

/** Shares 100 − control evenly across the variants with two decimals; the remainder goes to the first variant. */
export function splitEvenly(draft: MessagesDraft): MessagesDraft {
  const available = Math.max(0, 10_000 - cents(draft.control.percent));
  const each = Math.floor(available / draft.variants.length);
  const rest = available - each * draft.variants.length;
  return { ...draft, variants: draft.variants.map((v, i) => ({ ...v, percent: (each + (i === 0 ? rest : 0)) / 100 })) };
}

/** Variant and control shares together, rounded to two decimals. */
export const totalPercent = (draft: MessagesDraft): number => (draft.variants.reduce((s, v) => s + cents(v.percent), 0) + cents(draft.control.percent)) / 100;

export interface DraftIssue {
  path: Array<string | number>;
  message: string;
}

export type DraftValidation = { ok: true; value: delivery.CampaignMessages; issues: [] } | { ok: false; issues: DraftIssue[] };

/** Checks the draft with the server's schema (`delivery.CampaignMessages`). */
export function validateMessagesDraft(draft: unknown): DraftValidation {
  const result = delivery.CampaignMessages.safeParse(draft);
  if (result.success) return { ok: true, value: result.data, issues: [] };
  return { ok: false, issues: result.error.issues.map((i) => ({ path: i.path.filter((p): p is string | number => typeof p !== 'symbol'), message: i.message })) };
}

/** Messages of the issues at exactly this path, first letter capitalised for display. */
export const issuesAt = (issues: readonly DraftIssue[], ...path: Array<string | number>): string[] =>
  issues
    .filter((i) => i.path.length === path.length && path.every((p, k) => i.path[k] === p))
    .map((i) => i.message.charAt(0).toUpperCase() + i.message.slice(1));
